import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, QrCode, RefreshCw, LogOut, Smartphone, MessageCircle, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_LABEL = {
  ready: { label: 'Connected', color: 'text-emerald-500', bg: 'bg-emerald-500/10', icon: CheckCircle2 },
  authenticating: { label: 'Connecting…', color: 'text-cyan-400', bg: 'bg-cyan-500/10', icon: Loader2 },
  qr: { label: 'Waiting for scan', color: 'text-amber-500', bg: 'bg-amber-500/10', icon: QrCode },
  disconnected: { label: 'Disconnected', color: 'text-red-500', bg: 'bg-red-500/10', icon: XCircle },
  unavailable: { label: 'Sidecar unreachable', color: 'text-red-500', bg: 'bg-red-500/10', icon: XCircle },
};

export default function WhatsAppIntegration() {
  const [status, setStatus] = useState({ state: 'disconnected', qr: null, phone: null, lastError: null });
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/whatsapp/web/status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      setStatus((s) => ({ ...s, state: 'unavailable', lastError: e.response?.data?.detail || 'Failed to reach backend' }));
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/whatsapp/cron/logs?limit=50`, { withCredentials: true });
      setLogs(data.rows || []);
    } catch { /* silently ignore — non-admins won't have access */ }
  };

  const testRun = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/cron/test-run`, {}, { withCredentials: true });
      toast.success('Test run queued — logs will refresh in a few seconds');
      setTimeout(load, 6000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test run failed'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/web/connect`, {}, { withCredentials: true });
      toast.success('Starting WhatsApp session — QR will appear shortly');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Connect failed'); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp Web and clear the session?')) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/web/disconnect`, {}, { withCredentials: true });
      toast.success('Disconnected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Disconnect failed'); }
    finally { setBusy(false); }
  };

  const meta = STATE_LABEL[status.state] || STATE_LABEL.disconnected;
  const Icon = meta.icon;
  const spin = status.state === 'authenticating';

  return (
    <div className="space-y-6" data-testid="whatsapp-integration">
      <div>
        <h1 className="text-3xl font-bold text-white mb-1" data-testid="whatsapp-title">WhatsApp Integration</h1>
        <p className="text-slate-400 text-sm">Connect your MANHA DIGITAL business number to WhatsApp Web and send installment reminders straight from the app.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-white text-base flex items-center">
            <MessageCircle className="h-5 w-5 mr-2 text-emerald-500" />
            Session Status
          </CardTitle>
          <div className={`flex items-center px-3 py-1 rounded-sm ${meta.bg}`} data-testid={`wa-state-${status.state}`}>
            <Icon className={`h-4 w-4 mr-2 ${meta.color} ${spin ? 'animate-spin' : ''}`} />
            <span className={`text-sm font-medium ${meta.color}`}>{meta.label}</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center text-sm text-slate-400">
                <Smartphone className="h-4 w-4 mr-2 text-slate-500" />
                Connected as:
                <span className="ml-2 text-white font-medium" data-testid="wa-phone">{status.phone || '—'}</span>
              </div>
              {status.lastError && (
                <div className="text-xs text-red-400 border border-red-500/40 bg-red-500/10 rounded-sm p-2" data-testid="wa-error">
                  {status.lastError}
                </div>
              )}
              <div className="flex space-x-2 pt-2">
                {status.state !== 'ready' && (
                  <Button onClick={connect} disabled={busy}
                    data-testid="wa-connect-button"
                    className="bg-emerald-600 hover:bg-emerald-700">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <QrCode className="h-4 w-4 mr-2" />}
                    Connect / Scan QR
                  </Button>
                )}
                <Button onClick={load} variant="ghost" className="text-slate-300 hover:bg-slate-800"
                  data-testid="wa-refresh-button">
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
                {status.state === 'ready' && (
                  <Button onClick={disconnect} disabled={busy}
                    data-testid="wa-disconnect-button"
                    variant="destructive" className="bg-red-600 hover:bg-red-700">
                    <LogOut className="h-4 w-4 mr-2" /> Disconnect
                  </Button>
                )}
              </div>
              <div className="pt-4 space-y-2 text-xs text-slate-400 border-t border-slate-800 mt-4">
                <p><b className="text-slate-200">How it works</b></p>
                <ol className="list-decimal list-inside space-y-1 pl-1">
                  <li>Click <b>Connect / Scan QR</b>. Wait for the QR to appear.</li>
                  <li>On your phone, open WhatsApp → <b>Settings → Linked Devices → Link a Device</b>.</li>
                  <li>Scan the QR displayed on the right. Status will switch to <b>Connected</b>.</li>
                  <li>Once connected, every installment reminder is sent from your own WhatsApp number.</li>
                  <li>Click <b>Disconnect</b> anytime to log out and clear the session.</li>
                </ol>
              </div>
            </div>

            <div className="flex items-center justify-center bg-slate-800/40 border border-slate-700 rounded-sm min-h-[340px] p-4"
              data-testid="wa-qr-panel">
              {status.state === 'ready' ? (
                <div className="text-center">
                  <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-3" />
                  <div className="text-emerald-500 text-lg font-semibold">Session Active</div>
                  <div className="text-slate-400 text-sm mt-1">Ready to send WhatsApp messages.</div>
                </div>
              ) : status.state === 'qr' && status.qr ? (
                <div className="text-center">
                  <img src={status.qr} alt="WhatsApp QR" data-testid="wa-qr-image"
                    className="mx-auto rounded-sm bg-white p-2" style={{ width: 260, height: 260 }} />
                  <div className="text-xs text-slate-400 mt-3">Scan with WhatsApp mobile app</div>
                </div>
              ) : status.state === 'authenticating' ? (
                <div className="text-center">
                  <Loader2 className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-3" />
                  <div className="text-cyan-400 text-sm">Authenticating with WhatsApp…</div>
                </div>
              ) : (
                <div className="text-center text-slate-500 text-sm">
                  <QrCode className="h-12 w-12 mx-auto mb-2 text-slate-600" />
                  Click <span className="text-emerald-400">Connect / Scan QR</span> to begin.
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-white text-base flex items-center">
            <Clock className="h-5 w-5 mr-2 text-cyan-400" />
            Nightly Auto-Reminders
          </CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-400">Daily 09:00 PKT · <b>wa-due-tomorrow</b></span>
            <Button onClick={testRun} disabled={busy} data-testid="wa-test-run-button" className="bg-cyan-600 hover:bg-cyan-700">
              <Send className="h-4 w-4 mr-2" /> Test Run Now
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-400 mb-3">
            Each night at 09:00 Pakistan time, MANHA DIGITAL sends a reminder to every customer with an installment due <b>tomorrow</b>. Preferred channel: connected WhatsApp Web → Twilio → wa.me link.
          </div>
          {logs.length === 0 ? (
            <div className="text-slate-500 text-center py-6 text-sm border border-dashed border-slate-800 rounded-sm" data-testid="wa-logs-empty">
              No delivery logs yet. Click <b>Test Run Now</b> to send today's due-tomorrow batch immediately.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-800">
                    <th className="text-left py-2 pr-3">When</th>
                    <th className="text-left pr-3">Customer</th>
                    <th className="text-left pr-3">Mobile</th>
                    <th className="text-left pr-3">Installment #</th>
                    <th className="text-left pr-3">Amount</th>
                    <th className="text-left pr-3">Channel</th>
                    <th className="text-left pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.slice(0, 20).map((l) => (
                    <tr key={l.id} className="border-b border-slate-800/60" data-testid="wa-log-row">
                      <td className="py-2 pr-3 text-slate-500">{l.at ? new Date(l.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="pr-3 text-white">{l.customer_name || '—'}</td>
                      <td className="pr-3 text-slate-300">{l.mobile || '—'}</td>
                      <td className="pr-3 text-slate-300">{l.installment_no || '—'}</td>
                      <td className="pr-3 text-amber-500">PKR {(l.amount || 0).toLocaleString()}</td>
                      <td className="pr-3">
                        <span className={`px-2 py-0.5 rounded-sm uppercase ${
                          l.channel === 'whatsapp_web' ? 'bg-emerald-500/20 text-emerald-500' :
                          l.channel === 'whatsapp' ? 'bg-emerald-500/20 text-emerald-400' :
                          l.channel === 'wa_link' ? 'bg-slate-700/40 text-slate-300' :
                          'bg-red-500/20 text-red-500'
                        }`}>{l.channel || '—'}</span>
                      </td>
                      <td className="pr-3">
                        {l.sent
                          ? <span className="text-emerald-500">✓ Sent</span>
                          : <span className="text-red-500 truncate" title={l.error}>✕ {l.error ? l.error.slice(0, 40) : 'Failed'}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white text-base">Reminder Broadcasts</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-400">
          Use the existing <a href="/whatsapp-reminders" className="text-amber-500 hover:underline">WhatsApp Reminders</a> screen to broadcast messages. When a session is connected, messages route through your own number automatically; otherwise the app falls back to Twilio (if configured) or wa.me links.
        </CardContent>
      </Card>
    </div>
  );
}
